import { useState } from 'react';
import Amount, { CostLabel, IconText } from './Amount';
import CollapsedCard from './CollpasedCard';
import { CardFace } from './CardOverlay';
import RowCard from './RowCard';
import { AuctionGrid } from './SinceLastTurn';
import { SortedMilitaryHand } from './PlayerArea';
const TechnologyConverter = require('@tta/engine/Utilities/Converters/TechnologyConverter');
const CardConverter = require('@tta/engine/Utilities/Converters/CardConverter');
const { ShownName } = require('@tta/engine/Utilities/ShownNames');

// Decisions to make (mostly from events), where the actions usually are, so the board stays in view.
// Most decisions list their answers, shown like action buttons (in sections, with costs). Some are answered on the
// board or in steps (decision.ui): choosing cards to discard, moving a worker, taking cards from the card row.
// Nothing more about the game will come to light before choosing, so there is no point waiting. Frozen: the game is
// shown as it was when the decision was asked.
// Auction: a territory being auctioned (Players: the players' names, for its bids)
// LouvreTokens: each player's tokens on the Louvre Museum, which they may spend while the game waits on them
function DecisionPanel({Decisions, Frozen, GameEngine, ShowNames, Error, MilitaryHands, BoardSelection, Auction, Players, PlayerColors, LouvreTokens})
{
    // (once per player deciding now)
    const louvre = [...new Set(Decisions.filter(decision => !decision.waitingFor).map(decision => decision.player))]
        .filter(player => LouvreTokens?.[player] > 0);
    return (
        <div className="tta-decisions">
            {Decisions.map(decision =>
                <div key={decision.id} className="tta-decision">
                    <div className="tta-decision-title">
                        {ShowNames && <span>{decision.playerName}: </span>}{decision.event}
                    </div>
                    {/* (Taking cards for International Agreement says it all with the budget left and its buttons) */}
                    {/* (Defending: its own line, the card and both strengths) */}
                    {decision.ui !== 'agreementTake' && decision.ui !== 'defend' && <div><IconText text={decision.prompt}/></div>}
                    {/* Blocking decisions go one at a time, in turn order: this one waits for an earlier one */}
                    {decision.waitingFor && <div className="tta-waiting">{decision.waitingFor}.</div>}
                    {!decision.waitingFor && <div style={{ marginTop: '6px' }}>
                        {decision.type === 'planning' && <PlanningCards Decision={decision} GameEngine={GameEngine}/>}
                        {decision.ui === 'chooseCards' && <ChooseCards Decision={decision} Hand={MilitaryHands[decision.player]} GameEngine={GameEngine}/>}
                        {decision.ui === 'moveWorker' && <MoveWorker Decision={decision} GameEngine={GameEngine} Selection={BoardSelection}/>}
                        {decision.ui === 'boardPick' && <BoardPick Decision={decision} GameEngine={GameEngine} Selection={BoardSelection}/>}
                        {decision.ui === 'himejiWar' && <HimejiWar Decision={decision} GameEngine={GameEngine} PlayerColors={PlayerColors}/>}
                        {decision.ui === 'pickCard' && <PickCard Decision={decision} GameEngine={GameEngine}/>}
                        {decision.ui === 'agreementTake' && <AgreementTake Decision={decision} GameEngine={GameEngine}/>}
                        {decision.ui === 'bid' && <Bid Decision={decision} GameEngine={GameEngine} Auction={Auction} Players={Players} PlayerColors={PlayerColors}/>}
                        {decision.ui === 'warTechnology' && <WarTechnology Decision={decision} GameEngine={GameEngine}/>}
                        {decision.ui === 'defend' && <Defend Decision={decision} Hand={MilitaryHands[decision.player]} GameEngine={GameEngine} PlayerColors={PlayerColors}/>}
                        {/* (paying for the territory won: the auction as it went, then the units to sacrifice) */}
                        {decision.ui === 'sacrifice' && Auction && <AuctionView Auction={Auction} Players={Players} PlayerColors={PlayerColors}/>}
                        {decision.ui === 'sacrifice' && <Sacrifice Decision={decision} GameEngine={GameEngine}/>}
                        {!decision.ui && decision.type !== 'planning' && <ListedAnswers Decision={decision} GameEngine={GameEngine}/>}
                    </div>}
                </div>)}
            {louvre.map(player =>
                <div key={player} style={{ marginTop: '6px' }}>
                    <button className="tta-button" onClick={() => GameEngine.submitMove({ type: 'LouvreSpend', player })}>
                        <IconText text={`${ShowNames ? `${Decisions.find(d => d.player === player)?.playerName}: ` : ''}Louvre Museum: 1 blue token → 2 resources (${LouvreTokens[player]} left)`}/>
                    </button>
                </div>)}
            {Frozen && Decisions.some(decision => !decision.waitingFor) &&
                <div className="tta-muted" style={{ fontSize: '11px', marginTop: '4px' }}>
                    You see the game as it was when this was asked; the rest appears once you have chosen.
                </div>}
            {Error && <div style={{ color: '#b13a2a', fontWeight: 'bold' }}>{Error}</div>}
        </div>
    );
}

// An answer as an action button: its label, cost and how the cost was changed (one that cannot be chosen now, e.g. a
// revolution once a civil action is spent, greyed out with the reason)
const AnswerButton = ({option, onClick, title}) =>
    <button className="tta-button" onClick={onClick} disabled={option.disabled} title={option.disabled ? option.reason : title ?? option.text}>
        <IconText text={option.label}/>
        {option.cost && <span className="tta-button-cost"> · <CostLabel cost={option.cost}/></span>}
        {option.costNote && <span className="tta-cost-note"> ({option.costNote})</span>}
    </button>;

// Listed answers, grouped into their sections (e.g. Increase Population / Build / Develop), unsectioned ones last
function ListedAnswers({Decision, GameEngine})
{
    const sections = [];
    Decision.options.forEach(option => {
        const key = option.section ?? '';
        const section = sections.find(s => s.key === key) ?? (sections.push({ key, options: [] }), sections.at(-1));
        section.options.push(option);
    });
    sections.sort((a, b) => (a.key === '') - (b.key === ''));
    return sections.map(section =>
        <div key={section.key} className="tta-action-line">
            {section.key && <span className="tta-action-title">{section.key}:</span>}
            {section.options.map((option, index) =>
                <AnswerButton key={index} option={option} onClick={() => GameEngine.submitMove(option.move)}/>)}
        </div>);
}

// Development of Planning: the drawn events as cards; clicking one applies it to the player's civilization alone
function PlanningCards({Decision, GameEngine})
{
    return (
        <div className="tta-choice-buttons">
            {Decision.options.map(option =>
                <CardFace key={option.value} card={CardConverter({ type: 'Event', name: option.value, age: option.age ?? 'Age A', id: -1 })}
                    title={`Apply ${option.label} to your civilization`} onClick={() => GameEngine.submitMove(option.move)} scale={0.7}/>)}
        </div>
    );
}

// Choosing a number of military cards (e.g. to discard): click to pick, then confirm
function ChooseCards({Decision, Hand, GameEngine})
{
    const [chosen, setChosen] = useState([]);
    const count = Math.min(Decision.context.count, Hand.length);
    const toggle = (id) => setChosen(current => current.includes(id) ? current.filter(c => c !== id) : [...current, id]);
    return (
        <div>
            <div className="tta-choice-buttons">
                {SortedMilitaryHand(Hand).map(card =>
                    <div key={card.id} className={`tta-pickable${chosen.includes(card.id) ? ' picked' : ''}`} onClick={() => toggle(card.id)}>
                        <CollapsedCard card={card}/>
                    </div>)}
            </div>
            <div style={{ marginTop: '6px' }}>
                <span className="tta-muted">{chosen.length} of {count} chosen </span>
                <button className="tta-button dark" disabled={chosen.length !== count}
                    onClick={() => GameEngine.submitMove(GameEngine.TryDecision(Decision.id, chosen).move)}>
                    Discard
                </button>
            </div>
        </div>
    );
}

// A card's cost to build, under it in its slot: after any changes to it (e.g. Masonry's discount), which are named
const SlotCost = ({Card}) =>
    <div className="tta-move-slot-cost">
        <CostLabel cost={`${Card.cost} resources`}/>
        {Card.adjustments.length > 0 &&
            <span className="tta-cost-note"> ({Card.printed}{Card.adjustments.map(a => ` ${a.amount > 0 ? '+' : '−'}${Math.abs(a.amount)} ${a.source}`).join('')})</span>}
    </div>;

// Moving a worker between any two cards, picked on the player's board (see GameEngine.ClickBoardCard): two slots, where
// from and where to, each the card (as in the card row) or a blank. Board clicks fill the highlighted slot (where from
// to start with, then where to); clicking a slot makes it the one they fill. Confirm and Move nothing below.
function MoveWorker({Decision, GameEngine, Selection})
{
    const mine = Selection?.decisionId === Decision.id ? Selection : null;
    const tried = mine?.tried;
    // Changes to the payment itself, once both are picked (each card's own cost is under it)
    const details = tried?.costDetails;
    const slot = (which, name, label) =>
        <div className={`tta-move-slot${mine?.active === which ? ' active' : ''}`} title={`Click here, then on your board, to pick ${label.toLowerCase()}`}
            onClick={() => GameEngine.SetBoardSlot(which)}>
            <div className="tta-move-slot-label">{label}</div>
            <RowCard card={name ? TechnologyConverter(name) : null}/>
            {mine?.costs[which] && <SlotCost Card={mine.costs[which]}/>}
        </div>;
    return (
        <div>
            <div className="tta-action-line" style={{ alignItems: 'flex-end' }}>
                {slot('from', mine?.from, 'From')}
                <span className="tta-move-arrow">→</span>
                {slot('to', mine?.to, 'To')}
            </div>
            {tried && !tried.ok && <div className="tta-move-problem">{tried.error}</div>}
            <div className="tta-action-line" style={{ marginTop: '6px' }}>
                <button className="tta-button dark" disabled={!tried?.ok} onClick={() => GameEngine.submitMove(tried.move)}>
                    {tried?.ok && tried.cost > 0 ? <>Confirm and pay <CostLabel cost={`${tried.cost} resources`}/></> : tried?.ok ? 'Confirm (free)' : 'Confirm'}
                </button>
                {/* Changes to the payment itself, not to either card (e.g. an action card's) */}
                {details?.payment.length > 0 &&
                    <span className="tta-cost-note">({details.payment.map(a => `${a.amount > 0 ? '+' : '−'}${Math.abs(a.amount)} ${a.source}`).join(', ')})</span>}
                <button className="tta-button" onClick={() => GameEngine.submitMove(GameEngine.TryDecision(Decision.id, null).move)}>Move nothing</button>
            </div>
        </div>
    );
}

// Answers that are cards (e.g. the territories Suez Canal could colonize): each as its card, a little smaller,
// clicked to pick it (a dashed outline), then the button locks it in; the answers without a card (e.g. "No") as buttons
function PickCard({Decision, GameEngine})
{
    const [picked, setPicked] = useState(null);
    const chosen = Decision.options.find(option => option.card && option.value === picked);
    return (
        <div>
            <div className="tta-action-line" style={{ alignItems: 'flex-end' }}>
                {Decision.options.filter(option => option.card).map(option =>
                    <CardFace key={option.label} card={CardConverter(option.card)} scale={0.75} selected={option.value === picked} dashed
                        title={`Click to pick: ${option.label}`} onClick={() => setPicked(option.value === picked ? null : option.value)}/>)}
            </div>
            <div className="tta-action-line" style={{ marginTop: '6px' }}>
                <button className="tta-button dark" disabled={!chosen} onClick={() => GameEngine.submitMove(chosen.move)}>
                    {chosen ? ShownName(chosen.label) : 'Pick a card'}</button>
                {Decision.options.filter(option => !option.card).map(option =>
                    <button key={option.label} className="tta-button" onClick={() => GameEngine.submitMove(option.move)}>{ShownName(option.label)}</button>)}
            </div>
        </div>
    );
}

// Himeji Castle in a war being resolved: the war (its card, hover for it), who against whom with their strengths now,
// then each unit that could be added with what the war comes to with it (the other side may add one too)
function HimejiWar({Decision, GameEngine, PlayerColors})
{
    const { card, aggressor, defender, strengths, otherMayAdd } = Decision.context;
    const names = GameEngine.gameState.playerStates.map(p => p.name);
    const me = Decision.player;
    const other = me === aggressor ? defender : aggressor;
    const name = (number) => <b style={{ color: PlayerColors?.[number] }}>{names[number]}</b>;
    // (who wins, by how much, and what the loser gives the victor)
    const outcome = (result) => result.victor === null || result.victor === undefined
        ? <span>a tie: nothing happens</span>
        : <span>{names[result.victor]} wins by {result.advantage}: {names[result.loser]} → <IconText text={result.takes}/> → {names[result.victor]}</span>;
    return (
        <div>
            <div className="tta-action-line tta-defend-header">
                {name(aggressor)}'s <span className="tta-card-token"><CollapsedCard card={CardConverter(card)}/></span> on {name(defender)}:
                <Amount value={strengths[aggressor]} kind="strength"/> vs <Amount value={strengths[defender]} kind="strength"/>
                <span className="tta-muted">({strengths[me] === strengths[other] ? 'a tie' : `${names[strengths[me] > strengths[other] ? me : other]} ahead by ${Math.abs(strengths[me] - strengths[other])}`} as it stands)</span>
            </div>
            <div className="tta-action-line">
                {Decision.options.map(option =>
                    <button key={option.label} className="tta-button" onClick={() => GameEngine.submitMove(option.move)}
                        title={option.value ? `${option.value} is sacrificed after` : undefined}>
                        {option.value ? <>{ShownName(option.value)} <Amount value={option.strength} kind="strength" signed/></> : ShownName(option.label)}
                        <span className="tta-cost-note"> → {option.outcome && outcome(option.outcome)}</span>
                    </button>)}
            </div>
            {otherMayAdd && <div className="tta-muted" style={{ fontSize: '12px' }}>{names[other]} has Himeji Castle too, and may also add a unit.</div>}
        </div>
    );
}

// A card picked on a board (e.g. what Border Conflict destroys), as Freedom of Movement's: its slot shows the card
// clicked on the board (only those that can be picked are clickable), then the button to do it
// (a Raid: up to 2 slots, filled in the order clicked; the button names the answer they make, e.g. "Destroy Religion
// and Philosophy (3 resources)")
function BoardPick({Decision, GameEngine, Selection})
{
    const mine = Selection?.decisionId === Decision.id ? Selection : null;
    const tried = mine?.tried;
    const count = mine?.count ?? Decision.context?.pickCount ?? 1;
    return (
        <div>
            <div className="tta-action-line" style={{ alignItems: 'flex-end' }}>
                {Array.from({ length: count }, (_, index) =>
                    <div key={index} className={`tta-move-slot${index === Math.min(mine?.picked.length ?? 0, count - 1) ? ' active' : ''}`}
                        title="Click a card on the board to pick it; click a picked card to take it back">
                        <div className="tta-move-slot-label">{mine?.label ?? 'Pick'}{count > 1 ? ` ${index + 1}` : ''}</div>
                        <RowCard card={mine?.picked[index] ? TechnologyConverter(mine.picked[index]) : null}/>
                    </div>)}
            </div>
            {tried && !tried.ok && <div className="tta-move-problem">{tried.error}</div>}
            <div className="tta-action-line" style={{ marginTop: '6px' }}>
                <button className="tta-button dark" disabled={!tried?.ok} onClick={() => GameEngine.submitMove(tried.move)}>
                    {tried?.ok ? ShownName(`${mine.label} ${mine.matched?.label ?? mine.picked.join(' and ')}`) : 'Confirm'}
                </button>
            </div>
        </div>
    );
}

// International Agreement: "You may take 5 [CA] worth of cards at the cost of your next political phase.", then cards
// are clicked in the row (a taken card clicked again goes back); Confirm Taken Cards once something is taken, or Decline (which puts
// back anything taken)
function AgreementTake({Decision, GameEngine})
{
    const nothingTaken = Decision.context.budget === 5;
    return (
        <div>
            <div title="Click cards in the row to take them; click a taken card (or its place in the row) to put it back"><IconText text={Decision.prompt}/></div>
            <div className="tta-action-line" style={{ marginTop: '6px' }}>
                {!nothingTaken && <button className="tta-button dark" onClick={() => GameEngine.submitMove(GameEngine.TryDecision(Decision.id, 'done').move)}>Confirm Taken Cards</button>}
                <button className="tta-button" onClick={() => GameEngine.declineAgreement(Decision.player)}
                    title="Put back any cards taken, and keep your next political phase">Decline</button>
            </div>
        </div>
    );
}

// A territory's auction as it stands: its card, and the bids so far as a grid (a column per player, in bidding order),
// then what is asked now (children)
function AuctionView({Auction, Players, PlayerColors, children})
{
    const name = (player) => Players[player];
    const bids = (Auction.bids ?? []).map(entry => ({ player: name(entry.player), value: entry.pass ? { pass: true } : { bid: entry.bid } }));
    const colorOf = (player) => PlayerColors[Players.indexOf(player)];
    return (
        <div className="tta-action-line" style={{ alignItems: 'flex-start' }}>
            <CardFace card={Auction.territory} scale={0.85}/>
            <div>
                <AuctionGrid bids={bids} bidders={Auction.order.map(name)} colorOf={colorOf}/>
                {children && <div style={{ marginTop: '6px' }}>{children}</div>}
            </div>
        </div>
    );
}

// The auction for everyone not asked anything about it now, in a box like a decision's: the bids so far, and whom it
// waits on (to bid, or, the winner, to pay)
export function AuctionBox({Auction, Players, PlayerColors})
{
    const still = Auction.order.filter(player => !Auction.passed.includes(player));
    const paying = Auction.highest && still.length === 1;
    const waiting = paying ? Auction.highest.player : Auction.waitingFor;
    return (
        <div className="tta-decisions">
            <div className="tta-decision">
                <div className="tta-decision-title">{Auction.territory.name}</div>
                <AuctionView Auction={Auction} Players={Players} PlayerColors={PlayerColors}>
                    {waiting !== undefined && waiting !== null &&
                        <span className="tta-waiting">
                            Waiting for {Players[waiting]} to {paying ? `pay for it (units worth at least ${Auction.highest.bid})` : 'bid or pass'}.
                        </span>}
                </AuctionView>
            </div>
        </div>
    );
}

// Bidding for a territory: more than the bid so far, up to what the player could pay, or pass (for good). Bidding and
// passing are on a line of their own, apart, so neither is clicked for the other.
function Bid({Decision, GameEngine, Auction, Players, PlayerColors})
{
    const highest = Auction?.highest?.bid ?? 0;
    const [bid, setBid] = useState(highest + 1);
    const most = GameEngine.TryDecision(Decision.id, { pass: true }).most ?? 0;
    const tried = GameEngine.TryDecision(Decision.id, { bid });
    const controls = <>
        <div className="tta-action-line">
            <span className="tta-muted">You could pay up to {most}.</span>
            <button className="tta-button" disabled={bid <= highest + 1} onClick={() => setBid(bid - 1)}>−</button>
            <span style={{ minWidth: '2em', textAlign: 'center', fontWeight: 'bold' }}>{bid}</span>
            <button className="tta-button" disabled={bid >= most} onClick={() => setBid(bid + 1)}>+</button>
        </div>
        <div className="tta-action-line" style={{ marginTop: '8px', gap: '24px' }}>
            <button className="tta-button dark" disabled={!tried.ok} title={tried.error ?? undefined} onClick={() => GameEngine.submitMove(tried.move)}>Bid {bid}</button>
            <button className="tta-button" onClick={() => GameEngine.submitMove(GameEngine.TryDecision(Decision.id, { pass: true }).move)}>Pass</button>
        </div>
    </>;
    return Auction
        ? <AuctionView Auction={Auction} Players={Players} PlayerColors={PlayerColors}>{controls}</AuctionView>
        : controls;
}

// Paying for a territory won: units to sacrifice (as many of each as wanted), colonization cards to play, and with
// James Cook up to 2 other military cards, with Isabella of Castile resources for points; the total shows against the
// bid, and paying more is allowed
function Sacrifice({Decision, GameEngine})
{
    const [units, setUnits] = useState({});
    const [bonusCards, setBonusCards] = useState([]);
    const [cookCards, setCookCards] = useState([]);
    const [isabella, setIsabella] = useState(0);
    const player = GameEngine.gameState.playerStates[Decision.player];
    const military = player.technologies.filter(tech => ['Infantry', 'Cavalry', 'Artillery', 'Air Forces'].includes(tech.type) && tech.GetWorkerCount() > 0);
    const bonus = player.militaryHand.filter(card => card.type === 'Bonus');
    const others = player.leader?.name === 'James Cook' ? player.militaryHand.filter(card => card.type !== 'Bonus') : [];
    const payment = { units, bonusCards, cookCards, ...(isabella ? { isabella } : {}) };
    const tried = GameEngine.TryDecision(Decision.id, payment);
    const toggle = (list, set, id, limit = Infinity) => set(list.includes(id) ? list.filter(c => c !== id) : list.length < limit ? [...list, id] : list);
    const setCount = (name, count) => setUnits(current => ({ ...current, [name]: count }));
    return (
        <div>
            <div className="tta-action-line">
                <span className="tta-action-title">Units:</span>
                {military.map(tech => {
                    const count = units[tech.name] ?? 0;
                    return (
                        <span key={tech.name} className="tta-sacrifice-unit">
                            {ShownName(tech.name)}
                            <button className="tta-button" disabled={count === 0} onClick={() => setCount(tech.name, count - 1)}>−</button>
                            <b>{count}</b>/{tech.GetWorkerCount()}
                            <button className="tta-button" disabled={count >= tech.GetWorkerCount()} onClick={() => setCount(tech.name, count + 1)}>+</button>
                        </span>
                    );
                })}
            </div>
            {bonus.length > 0 &&
                <div className="tta-action-line">
                    <span className="tta-action-title">Colonization cards:</span>
                    {bonus.map(card =>
                        <div key={card.id} className={`tta-pickable${bonusCards.includes(card.id) ? ' picked' : ''}`} onClick={() => toggle(bonusCards, setBonusCards, card.id)}>
                            <CollapsedCard card={card}/>
                        </div>)}
                </div>}
            {others.length > 0 &&
                <div className="tta-action-line">
                    <span className="tta-action-title">James Cook (up to 2, +1 each):</span>
                    {others.map(card =>
                        <div key={card.id} className={`tta-pickable${cookCards.includes(card.id) ? ' picked' : ''}`} onClick={() => toggle(cookCards, setCookCards, card.id, 2)}>
                            <CollapsedCard card={card}/>
                        </div>)}
                </div>}
            {player.leader?.name === 'Isabella of Castile' &&
                <div className="tta-action-line">
                    <span className="tta-action-title">Isabella of Castile:</span>
                    {[[0, 0], [1, 1], [3, 2], [6, 3]].map(([resources, points]) =>
                        <button key={resources} className={`tta-button${isabella === resources ? ' dark' : ''}`}
                            disabled={resources > player.GetResources()} onClick={() => setIsabella(resources)}>
                            {resources === 0 ? 'None' : <><Amount value={resources} kind="resources"/>: <Amount value={points} kind="colonization" signed/></>}
                        </button>)}
                </div>}
            <div className="tta-action-line">
                <span>Worth <b>{tried.total ?? 0}</b> of the {Decision.context.bid} bid</span>
                {!tried.ok && <span className="tta-muted">{tried.error}</span>}
                <button className="tta-button dark" disabled={!tried.ok} onClick={() => GameEngine.submitMove(tried.move)}>Sacrifice and colonize</button>
            </div>
        </div>
    );
}

// Defending against an aggression: "[aggressor] plays [card]: 5 [strength] vs 2 [strength]", then the military cards,
// one under another, each ticked to use (up to the military actions, which are not spent) with what it adds; then the
// buttons. Defend is enabled once they reach the attacker's strength. Not defending is always a choice to make.
function Defend({Decision, Hand, GameEngine, PlayerColors})
{
    const [chosen, setChosen] = useState([]);
    // (Himeji Castle: a unit added, its strength + 3, sacrificed after)
    const [unit, setUnit] = useState(null);
    const units = Decision.context.himejiUnits ?? [];
    const { needed, limit, aggressor, card: played, aggressorStrength, defenderStrength } = Decision.context;
    const header = played &&
        <div className="tta-action-line tta-defend-header">
            <b style={{ color: PlayerColors?.[aggressor] }}>{GameEngine.gameState.playerStates[aggressor].name}</b> plays
            <span className="tta-card-token"><CollapsedCard card={CardConverter(played)}/></span>:
            <Amount value={aggressorStrength} kind="strength"/> vs <Amount value={defenderStrength} kind="strength"/>
        </div>;
    const valueOf = (card) => GameEngine.TryDecision(Decision.id, [card.id]).defense ?? 0;
    const tried = GameEngine.TryDecision(Decision.id, unit ? { cards: chosen, himejiUnit: unit } : chosen);
    const value = tried.defense ?? 0;
    const toggle = (id) => setChosen(current => current.includes(id) ? current.filter(c => c !== id)
        : current.length < limit ? [...current, id] : current);
    // The best the hand could do within the limit
    const best = Hand.map(valueOf).sort((a, b) => b - a).slice(0, limit).reduce((sum, v) => sum + v, 0)
        + Math.max(0, ...units.map(u => u.strength + 3));
    const decline = () => GameEngine.submitMove(GameEngine.TryDecision(Decision.id, []).move);
    if (best < needed)
    {
        return (
            <div className="tta-defend">{header}<div className="tta-action-line">
                <span className="tta-muted">
                    You need {needed} defense but can reach at most {best}
                    {Hand.length > 0 && ` (up to ${limit} card${limit === 1 ? '' : 's'}, one per military action)`}.
                </span>
                <button className="tta-button dark" onClick={decline}>Confirm: no defense</button>
            </div></div>
        );
    }
    return (
        <div className="tta-defend">
            {header}
            <div className="tta-defend-cards">
                {Hand.map(card => {
                    const picked = chosen.includes(card.id);
                    const full = !picked && chosen.length >= limit;
                    return (
                        <label key={card.id} className={`tta-defend-card${picked ? ' picked' : ''}${full ? ' full' : ''}`}>
                            <input type="checkbox" checked={picked} disabled={full} onChange={() => toggle(card.id)}/>
                            <CollapsedCard card={card}/>
                            <span className="tta-button-cost">+{valueOf(card)}</span>
                        </label>
                    );
                })}
            </div>
            {units.length > 0 &&
                <div className="tta-action-line tta-himeji-choice">
                    <span className="tta-action-title">Himeji Castle:</span>
                    <button className={`tta-button${unit === null ? ' dark' : ''}`} onClick={() => setUnit(null)}>No unit</button>
                    {units.map(u =>
                        <button key={u.name} className={`tta-button${unit === u.name ? ' dark' : ''}`} onClick={() => setUnit(u.name)}
                            title="Its strength + 3 is added to your defense, and it is sacrificed after">
                            {ShownName(u.name)} +{u.strength + 3}</button>)}
                </div>}
            <div className="tta-action-line" style={{ marginTop: '6px' }}>
                <span>Defense <b>{value}</b> of {needed} needed</span>
                <button className="tta-button dark" disabled={!tried.ok || value < needed} title={tried.error ?? undefined}
                    onClick={() => GameEngine.submitMove(tried.move)}>Defend</button>
                <button className="tta-button" onClick={decline}>Don't defend</button>
            </div>
        </div>
    );
}

// Winning War Over Technology: tick the special technologies to take (each counts its cost towards the advantage), see
// the science that leaves (as far as the loser has it) and confirm. One taken replaces the victor's own of its type.
function WarTechnology({Decision, GameEngine})
{
    const [chosen, setChosen] = useState([]);
    const { advantage, technologies, science } = Decision.context;
    const player = GameEngine.gameState.playerStates[Decision.player];
    const costOf = (names) => names.reduce((sum, name) => sum + technologies.find(t => t.name === name).cost, 0);
    const spent = costOf(chosen);
    const toggle = (name) => setChosen(current => current.includes(name) ? current.filter(n => n !== name) : [...current, name]);
    const tried = GameEngine.TryDecision(Decision.id, chosen);
    const scienceTaken = Math.max(0, Math.min(science, advantage - spent));
    return (
        <div>
            <div className="tta-choice-buttons">
                {technologies.map(tech => {
                    const picked = chosen.includes(tech.name);
                    const tooMuch = !picked && spent + tech.cost > advantage;
                    const replaces = player.technologies.find(t => t.type === tech.type);
                    return (
                        <label key={tech.name} className={`tta-defend-card${picked ? ' picked' : ''}${tooMuch ? ' full' : ''}`}
                            title={tooMuch ? `Worth ${tech.cost}: more than the ${advantage - spent} left` : undefined}>
                            <input type="checkbox" checked={picked} disabled={tooMuch} onChange={() => toggle(tech.name)}/>
                            {/* The technology as its card (hover for it) */}
                            <CollapsedCard card={TechnologyConverter(tech.name)}/>
                            <span className="tta-button-cost"><CostLabel cost={`${tech.cost} science`}/></span>
                            {replaces && <span className="tta-cost-note">replaces your <span className="tta-card-token"><CollapsedCard card={TechnologyConverter(replaces.name)}/></span></span>}
                        </label>
                    );
                })}
            </div>
            <div className="tta-action-line" style={{ marginTop: '6px' }}>
                <span>
                    Advantage {advantage}{spent > 0 && <> − {spent} in technologies</>}: you take <CostLabel cost={`${scienceTaken} science`}/>
                    {advantage - spent > science && <span className="tta-muted"> (all they have)</span>}
                </span>
                <button className="tta-button dark" disabled={!tried.ok} title={tried.error ?? undefined}
                    onClick={() => GameEngine.submitMove(tried.move)}>Confirm</button>
            </div>
        </div>
    );
}

export default DecisionPanel;
